import { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Footer from './components/Footer';
import PullToRefresh from './components/PullToRefresh';
import GoogleOAuthButton from './components/GoogleOAuthButton';
import { getApiUrl } from './config';
import { saveAuthToken } from './utils/auth';

// Only allow same-origin relative paths as a post-login redirect target.
function safeNextPath(value) {
  if (!value || typeof value !== 'string') return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  return value;
}

function Login() {
  const [searchParams] = useSearchParams();
  const nextPath = safeNextPath(searchParams.get('next'));
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        getApiUrl('auth/login'),
        {
          username: formData.username,
          password: formData.password,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      // Extract access_token from response
      const { access_token } = response.data;

      if (access_token) {
        // Save token with issue timestamp using auth utility
        saveAuthToken(access_token);

        // Clear form
        setFormData({ username: '', password: '' });

        // Straight to the next= target if provided (e.g. /invitations/{token}),
        // otherwise to /dashboard. safeNextPath() above blocks
        // protocol-relative or absolute URLs.
        window.location.href = nextPath || '/dashboard';
      } else {
        setError('No access token received from server');
      }
    } catch (err) {
      if (err.response) {
        setError(
          `Login failed: ${err.response.data.message || err.response.statusText}`,
        );
      } else if (err.request) {
        setError('Network error: Could not connect to server');
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    window.location.reload();
  }, []);

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-light dark:bg-background-dark">
        <header className="sticky top-0 z-50 w-full border-b border-gray-200/50 dark:border-gray-800/50 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-sm">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <Link to="/" className="flex items-center gap-4">
                <img
                  src="/favicon-32x32.png"
                  alt="Dissona"
                  className="size-6"
                />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Dissona
                </h2>
              </Link>
            </div>
          </div>
        </header>

        {/* Login Form */}
        <div className="flex-grow flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            <div className="bg-white dark:bg-background-dark/50 border border-gray-200 dark:border-gray-800 rounded-lg p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">
                Login
              </h2>

              {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {error}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex h-12 items-center justify-center rounded-lg bg-primary px-6 text-base font-bold text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
              </form>

              <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
                <Link to="/forgotten-password" className="link">
                  Forgotten your password?
                </Link>
              </p>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white dark:bg-background-dark/50 px-2 text-gray-500 dark:text-gray-400">
                    Or
                  </span>
                </div>
              </div>

              {/* Google OAuth Button */}
              <GoogleOAuthButton
                flowType="login"
                text="Continue with Google"
                intent="login"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </PullToRefresh>
  );
}

export default Login;
